export const getCarrierUser = /* GraphQL */ `
  query GetCarrierUser($id: ID!) {
    getCarrierUser(id: $id) {
      id
      status
      cognitoId
      email
      contactEmail
      unsubscribed
      firstName
      lastName
      phone
      userType
      driverCode
      reloadYardCarriers {
        items {
          id
          owner
          groups
          active
          carrierUserId
          carrierId
          # The carrier's own address decides whether this user is that
          # carrier's primary account — see isCarrierAdmin, which the edit and
          # delete controls both gate on.
          carrier {
            id
            email
            normalizedEmail
          }
          reloadYardId
          businessName
          firstName
          lastName
          email
          phone
          type
          description
          acl
          photo {
            bucket
            region
            key
            type
          }
          attachments {
            bucket
            region
            key
            type
            createdBy
            createdAt
          }
          notes
          blockAccess
          createdAt
          updatedAt
        }
        nextToken
      }
      createdAt
      updatedAt
    }
  }
`;

export const getCarrier = /* GraphQL */ `
  query GetCarrier($id: ID!) {
    getCarrier(id: $id) {
      id
      active
      businessName
      dbaName
      email
      phone
      identifiers {
        scheme
        value
      }
      # Which company this entry was placed on, stamped by handleCarrierChange.
      # CompanyProfileGate routes the profile edit by it, so an entry read
      # without it is one that reads as unplaced for good — and the form saves
      # the yard's own copy instead of the company every yard shares.
      companyId
      reloadYardId
      users {
        items {
          id
          active
          # Read by isCarrierProfileSelfManaged: a blocked dispatcher cannot sign
          # in, so their row must not lock the manager out of the profile.
          blockAccess
          carrierUserId
          # user {
          #   id
          #   status
          #   cognitoId
          #   email
          #   firstName
          #   lastName
          # }
          # carrierId
          reloadYardId
          businessName
          firstName
          lastName
          # Denormalized contact details, read by the Users tab on the manager's
          # carrier form: the tab lists the junction rows straight off this
          # record rather than refetching each person.
          email
          phone
          type
        }
        nextToken
      }
      createdAt
      updatedAt
    }
  }
`;

// The carrier portal's user list: one page of a carrier entry's junction rows,
// each carrying the person it belongs to. Only what a list line and its status
// icon need — the profile refetches the user in full.
export const reloadYardCarrierUsersByCarrier = /* GraphQL */ `
  query ReloadYardCarrierByCarrierByActive(
    $carrierId: ID
    $active: ModelStringKeyConditionInput
    $limit: Int
    $nextToken: String
  ) {
    reloadYardCarrierByCarrierByActive(
      carrierId: $carrierId
      active: $active
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        active
        deleted
        blockAccess
        type
        businessName
        carrierId
        reloadYardId
        user {
          id
          status
          email
          firstName
          lastName
          phone
          driverCode
        }
      }
      nextToken
    }
  }
`;

// App.js user login query
export const carrierUserByEmail = /* GraphQL */ `
  query CarrierUserByEmail(
    $email: String
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    carrierUserByEmail(
      email: $email
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        cognitoId
        email
        firstName
        lastName
        phone
        reloadYardCarriers {
          items {
            id
            active
            carrierId
            reloadYardId
            businessName
            # The denormalized user fields ride along so junction updates built
            # by spreading these rows carry every member of the composite sort
            # keys (active/type/businessName/lastName) — a spread missing
            # lastName writes a broken "active#..." GSI attribute.
            firstName
            lastName
            email
            phone
            type
            acl
            groups
            blockAccess
            # Names the yard switcher and the remove-transload dialog, which read
            # the junctions straight off the app user. Only what a menu row
            # needs — the selected yard is refetched in full by doGetReloadYard.
            reloadYard {
              id
              name
            }
          }
        }
      }
      nextToken
    }
  }
`;

export const carrierUserByEmailMini = /* GraphQL */ `
  query CarrierUserByEmail(
    $email: String
    $sortDirection: ModelSortDirection
    $filter: ModelCarrierUserFilterInput
    $limit: Int
    $nextToken: String
  ) {
    carrierUserByEmail(
      email: $email
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        cognitoId
        firstName
        lastName
        reloadYardCarriers {
          items {
            id
            active
          }
        }
      }
      nextToken
    }
  }
`;

/**
 * Every carrier in a yard — archived ones included — reduced to the fields the
 * uniqueness check compares.
 *
 * Runs carrierByActiveByName with the partition key alone. That index sorts on
 * `active#businessName`, so omitting the sort condition is exactly what pulls
 * archived carriers into the check: an archived carrier still holds its email
 * and phone, and creating a second record for it is the duplicate the check
 * exists to stop.
 *
 * A per-field index would read less, but the normalized fields are new and every
 * carrier predating them stores null — an indexed lookup would miss the whole
 * existing book. Comparing the yard's carriers in memory finds those too, and
 * carrier creation is rare enough to afford the read.
 */
// The manager's carrier-user list, one tab's page straight off the DynamoDB
// GSI (reloadYardId / active#type#businessName#lastName). The OpenSearch query
// the list previously used indexes writes asynchronously, so a refetch right
// after a delete still showed the deleted row; the GSI reflects the write
// immediately. Selection mirrors what CarrierUserList's grid reads — the
// profile refetches the user in full.
export const reloadYardCarrierByActiveByTypeByName = /* GraphQL */ `
  query ReloadYardCarrierByActiveByTypeByName(
    $reloadYardId: ID
    $activeTypeBusinessNameLastName: ModelCarrierUserReloadYardCarrierReloadYardCarrierByActiveByTypeByNameCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelCarrierUserReloadYardCarrierFilterInput
    $limit: Int
    $nextToken: String
  ) {
    reloadYardCarrierByActiveByTypeByName(
      reloadYardId: $reloadYardId
      activeTypeBusinessNameLastName: $activeTypeBusinessNameLastName
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        active
        deleted
        blockAccess
        type
        businessName
        firstName
        lastName
        email
        phone
        carrierUserId
        carrierId
        reloadYardId
        user {
          id
          status
          email
          firstName
          lastName
          phone
          userType
          driverCode
        }
      }
      nextToken
    }
  }
`;

export const carrierIdentitiesByReloadYard = /* GraphQL */ `
  query CarrierByActiveByName(
    $reloadYardId: ID
    $limit: Int
    $nextToken: String
  ) {
    carrierByActiveByName(
      reloadYardId: $reloadYardId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        active
        businessName
        dbaName
        email
        phone
        normalizedEmail
        normalizedPhone
        # The strongest half of the duplicate check — see findCarrierConflict.
        identifiers {
          scheme
          value
        }
      }
      nextToken
    }
  }
`;

// The carrier's own company record, as its Company Profile form reads it.
//
// The generated getCarrierCompany also selects the `yards` connection — every
// yard entry the company has been placed under. The profile form needs none of
// them: it edits the company, and the numbers it shows are the company's. A
// carrier user holds only the groups of the yards they work, so that list would
// also come back silently short of the company's real reach, which is worse than
// not asking for it.
export const getCarrierCompany = /* GraphQL */ `
  query GetCarrierCompany($id: ID!) {
    getCarrierCompany(id: $id) {
      id
      groups
      businessName
      email
      phone
      normalizedEmail
      normalizedPhone
      identifiers {
        scheme
        value
      }
      createdAt
      updatedAt
    }
  }
`;

// Every carrier entry platform-wide, as the admin dashboard's company backfill
// reads them: enough to tell a placed entry from an unplaced one and to work
// through them oldest first. Only an admin session can run this — a yard's own
// group would see its own rows and silently nothing else.
export const listCarriersForCompanyBackfill = /* GraphQL */ `
  query ListCarriers($limit: Int, $nextToken: String) {
    listCarriers(limit: $limit, nextToken: $nextToken) {
      items {
        id
        businessName
        companyId
        reloadYardId
        createdAt
      }
      nextToken
    }
  }
`;

// How many companies exist, for the backfill's before-and-after count. The rows
// themselves are not wanted, but AppSync has no count — so this asks for the
// narrowest field there is and pages.
export const listCarrierCompanyIds = /* GraphQL */ `
  query ListCarrierCompanys($limit: Int, $nextToken: String) {
    listCarrierCompanys(limit: $limit, nextToken: $nextToken) {
      items {
        id
      }
      nextToken
    }
  }
`;

// Does any shipment name this carrier? Asked by the manager's carrier delete
// (DeepDeleteButton) before it lets a Carrier row go: a shipment's carrierId
// has no ON DELETE anything, so a deleted carrier would leave the pointer
// dangling and the carrier portal's by-carrier GSIs keyed on a dead id. Only
// existence is the question, so the selection is the narrowest field there is
// and the caller passes limit 1 — the composite sort key is left off, which
// makes the query a plain "any row under this hash key".
export const inboundShipmentIdsByCarrier = /* GraphQL */ `
  query InboundShipmentByCarrierByStatusByDates(
    $carrierId: ID
    $limit: Int
    $nextToken: String
  ) {
    inboundShipmentByCarrierByStatusByDates(
      carrierId: $carrierId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
      }
      nextToken
    }
  }
`;

export const outboundShipmentIdsByCarrier = /* GraphQL */ `
  query OutboundShipmentByCarrierByStatusByDates(
    $carrierId: ID
    $limit: Int
    $nextToken: String
  ) {
    outboundShipmentByCarrierByStatusByDates(
      carrierId: $carrierId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
      }
      nextToken
    }
  }
`;

// Every carrier entry platform-wide, as the admin dashboard's dbaName backfill
// reads them: the regulatory numbers to ask FMCSA with, and the two names it
// compares the register's answer against. Admin session only, as above.
export const listCarriersForDbaBackfill = /* GraphQL */ `
  query ListCarriers($limit: Int, $nextToken: String) {
    listCarriers(limit: $limit, nextToken: $nextToken) {
      items {
        id
        active
        businessName
        dbaName
        identifiers {
          scheme
          value
        }
      }
      nextToken
    }
  }
`;
